/**
 * Cross-browser camera + mic access for hospital QR bedside calls.
 * Supports Chrome, Safari (iOS/macOS), Firefox, Edge, Samsung Internet.
 * Legacy: webkit/moz getUserMedia. In-app browsers (WhatsApp/FB) often block WebRTC.
 */

function ua() {
  return (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
}

export function detectPatientBrowser() {
  const u = ua();
  const isIOS = /iPad|iPhone|iPod/.test(u) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(u);
  const isSafari = /Safari/i.test(u) && !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS/i.test(u);
  const isChrome = /Chrome|CriOS/i.test(u) && !/Edg|OPR/i.test(u);
  const isFirefox = /Firefox|FxiOS/i.test(u);
  const isEdge = /Edg/i.test(u);
  const isSamsung = /SamsungBrowser/i.test(u);
  // In-app WebViews — video call often fails; ask patient to open in real browser
  const isInApp =
    /\bFBAN\b|\bFBAV\b|Instagram|Line\/|WhatsApp|MicroMessenger|Twitter|LinkedInApp|Snapchat|Pinterest|TikTok/i.test(u)
    || (/\bwv\b/i.test(u) && isAndroid && /Version\/\d+\.\d+/i.test(u));

  let family = 'other';
  if (isInApp) family = 'inapp';
  else if (isSamsung) family = 'samsung';
  else if (isEdge) family = 'edge';
  else if (isFirefox) family = 'firefox';
  else if (isChrome) family = 'chrome';
  else if (isSafari || (isIOS && isSafari)) family = 'safari';
  else if (isIOS) family = 'safari';

  return { family, isIOS, isAndroid, isSafari, isChrome, isFirefox, isEdge, isSamsung, isInApp };
}

/** Polyfill mediaDevices.getUserMedia for older WebKit / Firefox. */
export function ensureMediaDevicesApi() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  if (!navigator.mediaDevices) {
    navigator.mediaDevices = {};
  }

  if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
    const legacy =
      navigator.getUserMedia
      || navigator.webkitGetUserMedia
      || navigator.mozGetUserMedia
      || navigator.msGetUserMedia;

    if (typeof legacy === 'function') {
      navigator.mediaDevices.getUserMedia = function getUserMedia(constraints) {
        return new Promise((resolve, reject) => {
          legacy.call(navigator, constraints, resolve, reject);
        });
      };
    }
  }

  return typeof navigator.mediaDevices.getUserMedia === 'function';
}

export async function queryMediaPermissionState() {
  if (!navigator?.permissions?.query) {
    return 'unknown';
  }
  try {
    // Safari often throws TypeError for camera/microphone names — treat as unknown
    const [cam, mic] = await Promise.all([
      navigator.permissions.query({ name: 'camera' }).catch(() => null),
      navigator.permissions.query({ name: 'microphone' }).catch(() => null),
    ]);
    const states = [cam?.state, mic?.state].filter(Boolean);
    if (!states.length) {
      return 'unknown';
    }
    if (states.includes('denied')) {
      return 'denied';
    }
    if (states.every((s) => s === 'granted')) {
      return 'granted';
    }
    if (states.includes('prompt')) {
      return 'prompt';
    }
    return 'unknown';
  } catch (_e) {
    return 'unknown';
  }
}

function stopStream(stream) {
  try {
    if (stream && stream.getTracks) {
      stream.getTracks().forEach((track) => {
        try { track.stop(); } catch (_e) { /* ignore */ }
      });
    }
  } catch (_e) {
    /* ignore */
  }
}

async function getUserMediaWithFallback() {
  const gum = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  const attempts = [
    { audio: true, video: true },
    // Some Android/WebViews reject bare `true` for video
    {
      audio: true,
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 },
      },
    },
    {
      audio: true,
      video: { facingMode: { ideal: 'user' } },
    },
    // Last: still require both for a real video call
    { audio: { echoCancellation: true }, video: true },
  ];

  let lastErr = null;
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      const stream = await gum(attempts[i]);
      return stream;
    } catch (err) {
      lastErr = err;
      const name = String(err?.name || '');
      // Don't keep trying if user denied / blocked
      if (
        name === 'NotAllowedError'
        || name === 'PermissionDeniedError'
        || name === 'SecurityError'
      ) {
        throw err;
      }
    }
  }
  throw lastErr || new Error('getUserMedia failed');
}

/**
 * Always calls getUserMedia so the browser Allow popup can open when allowed.
 * If the user previously tapped Block, most browsers will NOT show the popup again
 * (reason: 'blocked') until they change site settings.
 */
export async function ensurePatientMediaPermission() {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'unsupported' };
  }

  const browser = detectPatientBrowser();
  if (browser.isInApp) {
    return { ok: false, reason: 'inapp', browser };
  }

  if (!ensureMediaDevicesApi()) {
    return { ok: false, reason: 'unsupported', browser };
  }

  // HTTP (except localhost) blocks camera in modern browsers
  const host = window.location && window.location.hostname;
  const isLocal =
    host === 'localhost'
    || host === '127.0.0.1'
    || host === '[::1]';
  if (typeof window.isSecureContext === 'boolean' && !window.isSecureContext && !isLocal) {
    return { ok: false, reason: 'insecure', browser };
  }

  try {
    const stream = await getUserMediaWithFallback();
    stopStream(stream);
    return { ok: true, browser };
  } catch (err) {
    const name = String(err?.name || '');
    const msg = String(err?.message || '');
    const denied =
      name === 'NotAllowedError'
      || name === 'PermissionDeniedError'
      || name === 'SecurityError'
      || /permission|denied|notallowed/i.test(msg);
    const notFound =
      name === 'NotFoundError'
      || name === 'DevicesNotFoundError'
      || name === 'OverconstrainedError'
      || /not found|Requested device/i.test(msg);

    let reason = denied ? 'denied' : (notFound ? 'not_found' : 'error');
    if (denied) {
      const after = await queryMediaPermissionState();
      // Permissions API missing (Safari) → keep 'denied' so Allow & Call can re-prompt
      if (after === 'denied') {
        reason = 'blocked';
      } else if (after === 'unknown' && browser.isSafari) {
        // iOS Safari: after Block, often still unknown — show guide + Allow & Call
        reason = 'blocked';
      }
    }
    return { ok: false, reason, error: err, browser };
  }
}

/**
 * After user returns from settings — only auto-continue when we KNOW permission is granted.
 * Never call getUserMedia here on Safari/unknown (would re-open the Allow popup unexpectedly).
 */
export async function isPatientMediaAlreadyGranted() {
  if (!ensureMediaDevicesApi()) {
    return false;
  }
  const state = await queryMediaPermissionState();
  return state === 'granted';
}

/** Step-by-step unlock guide text for the current browser. */
export function patientPermissionGuideSteps(isTamil = false) {
  const { family, isIOS, isAndroid } = detectPatientBrowser();

  if (isTamil) {
    if (family === 'safari' || isIOS) {
      return [
        'Safari முகவரிப் பட்டியில் "aA" அல்லது பூட்டு ஐகானைத் தட்டவும்',
        'Website Settings → Camera & Microphone → Allow',
        'பின் Allow & Call என்பதைத் தட்டவும்',
      ];
    }
    if (isAndroid || family === 'chrome' || family === 'samsung' || family === 'edge') {
      return [
        'மேலே உள்ள முகவரிப் பட்டியில் பூட்டு / தகவல் ஐகானைத் தட்டவும்',
        'Permissions → Camera & Microphone → Allow',
        'பின் Allow & Call என்பதைத் தட்டவும்',
      ];
    }
    if (family === 'firefox') {
      return [
        'முகவரிப் பட்டியில் பூட்டு ஐகானைத் தட்டவும்',
        'Connection secure → More information → Permissions → Allow',
        'பின் Allow & Call என்பதைத் தட்டவும்',
      ];
    }
    return [
      'Browser முகவரிப் பட்டியில் பூட்டு / தகவல் ஐகானைத் தட்டவும்',
      'Camera மற்றும் Microphone-ஐ Allow ஆக மாற்றவும்',
      'பின் Allow & Call என்பதைத் தட்டவும்',
    ];
  }

  if (family === 'safari' || isIOS) {
    return [
      'In Safari, tap aA (or the lock) in the address bar',
      'Website Settings → Camera & Microphone → Allow',
      'Then tap Allow & Call',
    ];
  }
  if (isAndroid || family === 'chrome' || family === 'samsung' || family === 'edge') {
    return [
      'Tap the lock (or site info) icon in the address bar',
      'Permissions → Camera & Microphone → Allow',
      'Then tap Allow & Call',
    ];
  }
  if (family === 'firefox') {
    return [
      'Tap the lock icon in the address bar',
      'Connection secure → More information → Permissions → Allow',
      'Then tap Allow & Call',
    ];
  }
  return [
    'Tap the lock / site info icon in the address bar',
    'Set Camera and Microphone to Allow',
    'Then tap Allow & Call',
  ];
}

export function patientMediaPermissionMessage(reason) {
  if (reason === 'inapp') {
    return 'This app browser cannot run video call. Open this page in Chrome or Safari.';
  }
  if (reason === 'blocked') {
    return 'The Allow popup will not open until Camera is turned on. Use the steps below, then tap Allow & Call.';
  }
  if (reason === 'denied') {
    return 'Tap Allow & Call — then tap Allow in the browser popup.';
  }
  if (reason === 'not_found') {
    return 'No camera or mic found on this device.';
  }
  if (reason === 'unsupported') {
    return 'This browser does not support video call. Please use Chrome, Safari, Edge, or Firefox.';
  }
  if (reason === 'insecure') {
    return 'Video call needs a secure (https) link. Ask hospital staff for help.';
  }
  return 'Could not open camera. Please try again.';
}

export function patientMediaPermissionTitle(reason) {
  if (reason === 'inapp') {
    return 'Open in Chrome or Safari';
  }
  if (reason === 'blocked') {
    return 'Turn on Camera & Mic';
  }
  return 'Allow Camera & Mic';
}

export default ensurePatientMediaPermission;
